// processClipboard — a tiny, guarded wrapper over the async Clipboard API for
// the process editor's Copy/Paste. `navigator.clipboard` is absent in jsdom and
// can reject under a permissions policy or a non-secure context, so every access
// is feature-detected and every promise `.catch`-guarded — a clipboard failure
// must never throw into a click handler or leave a half-run edit.
//
// The two entry points take an optional `clip` override so tests inject a fake
// clipboard (and the app can too, e.g. a headless embedder); when omitted they
// resolve `navigator.clipboard` lazily at call time (not import time — it may be
// polyfilled after module load).

/** The minimal Clipboard surface we use — inject a fake in tests. */
export interface ClipboardLike {
  writeText(text: string): Promise<void>;
  readText(): Promise<string>;
}

function resolveClipboard(clip?: ClipboardLike): ClipboardLike | null {
  if (clip) return clip;
  const nav = typeof navigator !== "undefined" ? (navigator as Navigator) : undefined;
  const c = nav?.clipboard as Partial<ClipboardLike> | undefined;
  if (c && typeof c.writeText === "function" && typeof c.readText === "function") {
    return c as ClipboardLike;
  }
  return null;
}

/** Copy text to the clipboard. Resolves `true` on success, `false` when the
 *  clipboard is unavailable OR the write rejects (denied permission, etc.). */
export async function copyText(text: string, clip?: ClipboardLike): Promise<boolean> {
  const c = resolveClipboard(clip);
  if (!c) return false;
  try {
    await c.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Read text from the clipboard. Resolves the text, or `null` when the clipboard
 *  is unavailable OR the read rejects. */
export async function readText(clip?: ClipboardLike): Promise<string | null> {
  const c = resolveClipboard(clip);
  if (!c) return null;
  try {
    return await c.readText();
  } catch {
    return null;
  }
}

/** Split a pasted clipboard payload into step lines: trim, drop blank lines.
 *  Each surviving line is passed verbatim to `addStepAfter` (the recompile
 *  invariant is the last guard, so a malformed line is refused, not corrupting). */
export function clipboardStepLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}
